// ─────────────────────────────────────────────────────────────────────────
// Institute labels — the words the dashboard uses for the campus structure.
//
// One institute stores the same data whatever kind of campus it is
// (department → programme → classroom). Only the WORDS change: a school calls
// a programme a "Class" and a classroom a "Section". The institute's type
// comes from the server (signin / user/fetchRealtimeConnectionUrl; it is set
// once by the system when the institute is created and never changed from a
// client) and is kept in the `institute_type`
// cookie. Not set, or a value this build doesn't know = today's words.
//
// Use the hook in components (re-renders when an admin changes the type):
//     const L = useInstituteLabels();
//     <h1>{L.classrooms}</h1>
// ─────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { environment } from '@/env/env.prod';
import { getHeaders } from '@/api/requestHeaders';

// Same list as the server (Institute-Registery.js INSTITUTE_TYPES).
export const INSTITUTE_TYPES = [ 'university', 'college', 'school', 'training' ] as const;
export type InstituteType = typeof INSTITUTE_TYPES[ number ];

export interface InstituteLabels {
    department: string;
    departments: string;
    programme: string;
    programmes: string;
    classroom: string;
    classrooms: string;
    incharge: string;
    subject: string;
    subjects: string;
}

// Today's words — every institute without a type sees exactly these.
export const DEFAULT_LABELS: InstituteLabels = {
    department: 'Department',
    departments: 'Departments',
    programme: 'Programme',
    programmes: 'Programmes',
    classroom: 'Classroom',
    classrooms: 'Classrooms',
    incharge: 'Coordinator',
    subject: 'Subject',
    subjects: 'Subjects',
};

// Only what differs from today's words (agreed in the campus-structure demo).
const OVERRIDES: Record<InstituteType, Partial<InstituteLabels>> = {
    university: {
        incharge: 'Classroom in-charge',
    },
    college: {
        programme: 'Course', programmes: 'Courses',
        classroom: 'Class', classrooms: 'Classes',
        incharge: 'Class in-charge',
    },
    school: {
        // Department stays: science departments and senior / junior wings both fit it
        programme: 'Class', programmes: 'Classes',
        classroom: 'Section', classrooms: 'Sections',
        incharge: 'Class teacher',
    },
    training: {
        programme: 'Course', programmes: 'Courses',
        classroom: 'Batch', classrooms: 'Batches',
        incharge: 'Batch coordinator',
        subject: 'Module', subjects: 'Modules',
    },
};

export const INSTITUTE_TYPE_NAMES: Record<InstituteType, string> = {
    university: 'University',
    college: 'College',
    school: 'School',
    training: 'Training institute',
};

const COOKIE = 'institute_type';
const CHANGED_EVENT = 'institute-type-changed';

export function toInstituteType(value: unknown): InstituteType | null {
    return INSTITUTE_TYPES.includes(value as InstituteType) ? (value as InstituteType) : null;
}

export function getInstituteType(): InstituteType | null {
    return toInstituteType(Cookies.get(COOKIE));
}

export function labelsFor(type: InstituteType | null): InstituteLabels {
    return type ? { ...DEFAULT_LABELS, ...OVERRIDES[ type ] } : DEFAULT_LABELS;
}

// Store the type (null clears it) and tell mounted components.
export function storeInstituteType(value: unknown): void {
    const type = toInstituteType(value);
    if (type) Cookies.set(COOKIE, type);
    else Cookies.remove(COOKIE);
    window.dispatchEvent(new Event(CHANGED_EVENT));
}

// The current type, kept in step with storeInstituteType (server refresh,
// an admin's change).
export function useInstituteType(): InstituteType | null {
    const [ type, setType ] = useState<InstituteType | null>(getInstituteType);
    useEffect(() => {
        const onChange = () => setType(getInstituteType());
        window.addEventListener(CHANGED_EVENT, onChange);
        return () => window.removeEventListener(CHANGED_EVENT, onChange);
    }, []);
    return type;
}

export function useInstituteLabels(): InstituteLabels {
    return labelsFor(useInstituteType());
}

// Re-read the type from the server. A tab signed in before the type was
// set (or changed by another admin) catches up here. Failures keep the
// stored value.
export async function refreshInstituteType(): Promise<void> {
    try {
        const res = await fetch(`${environment.BASE_URL}/user/fetchRealtimeConnectionUrl`, {
            method: 'POST',
            headers: getHeaders(),
            body: '{}',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data && 'instituteType' in data) storeInstituteType(data.instituteType);
    } catch {
        // offline / server error: keep what we have
    }
}

// Admin / superadmin only (server checks CASL `update institute`).
